import { ADMIN_ENDPOINTS } from './endpoints.ts';
import {
  apiGet,
  apiPost,
  apiPatch,
  apiPut,
  apiDelete,
} from '../../api/oauth/helpers.ts';
import type {
  Organization,
  OrganizationMember,
  Integration,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  CreateIntegrationRequest,
  UpdateIntegrationRequest,
  AddUserToOrgRequest,
  UpdateMemberRoleRequest,
  UpdateOrgMetadataRequest,
  UpdateOrgSettingsRequest,
  ApiMessageResponse,
  OrgMemberApiItem,
} from '../types.ts';

export const getOrganizations = (): Promise<Organization[]> => {
  const url = ADMIN_ENDPOINTS.ORGANIZATIONS;
  return apiGet(url).then((data: Organization[] | { organizations?: Organization[] }) => {
    const list = Array.isArray(data)
      ? data
      : (data as { organizations?: Organization[] })?.organizations ?? [];
    return list as Organization[];
  });
};

export const getOrganizationById = (id: string): Promise<Organization> => {
  const url = ADMIN_ENDPOINTS.ORGANIZATION_BY_ID(id);
  return apiGet(url).then((data: Organization) => data);
};

export const createOrganization = (
  payload: CreateOrganizationRequest
): Promise<Organization> => {
  const url = ADMIN_ENDPOINTS.ORGANIZATIONS;
  return apiPost(url, payload).then((data: Organization) => data);
};

export const updateOrganization = (
  id: string,
  payload: UpdateOrganizationRequest
): Promise<Organization> => {
  const url = ADMIN_ENDPOINTS.ORGANIZATION_BY_ID(id);
  return apiPatch(url, payload).then((data: Organization) => data);
};

export const getOrgIntegrations = (orgId: string): Promise<Integration[]> => {
  const url = ADMIN_ENDPOINTS.ORG_INTEGRATIONS(orgId);
  return apiGet(url).then((data: Integration[] | { integrations?: Integration[] }) => {
    const raw = Array.isArray(data)
      ? data
      : (data as { integrations?: Integration[] })?.integrations ?? [];
    return raw as Integration[];
  });
};

export const createOrgIntegration = (
  orgId: string,
  payload: CreateIntegrationRequest
): Promise<{ data: Integration }> => {
  const url = ADMIN_ENDPOINTS.ORG_INTEGRATIONS(orgId);
  return apiPost(url, payload).then((data: Integration) => ({ data }));
};

export const updateOrgIntegration = (
  orgId: string,
  integrationId: string,
  payload: UpdateIntegrationRequest
): Promise<Integration> => {
  const url = ADMIN_ENDPOINTS.ORG_INTEGRATION_BY_ID(orgId, integrationId);
  return apiPatch(url, payload).then((data: Integration) => data);
};

/** API returns flat array: [{ id, username, email, first_name, last_name, role, is_default }, ...] */
export const getOrgMembers = (orgId: string): Promise<OrganizationMember[]> => {
  const url = ADMIN_ENDPOINTS.ORG_MEMBERS(orgId);
  return apiGet(url).then((data: OrgMemberApiItem[] | { members?: OrgMemberApiItem[] }) => {
    const raw: OrgMemberApiItem[] = Array.isArray(data)
      ? data
      : (data as { members?: OrgMemberApiItem[] })?.members ?? [];
    return raw.map((item: OrgMemberApiItem) => ({
      id: item.id,
      user_id: item.user_id ?? item.id,
      organization_id: orgId,
      role: (item.role as string) ?? 'report_viewer',
      is_default: Boolean(item.is_default),
      user: {
        id: item.id as number,
        username: (item.username as string) ?? '',
        email: (item.email as string) ?? '',
        first_name: (item.first_name as string) ?? '',
        last_name: (item.last_name as string) ?? '',
        is_active: item.is_active == null ? undefined : Boolean(item.is_active),
      },
    }));
  });
};

export const addUserToOrg = (
  orgId: string,
  payload: AddUserToOrgRequest
): Promise<OrganizationMember> => {
  const url = ADMIN_ENDPOINTS.ORG_MEMBERS(orgId);
  return apiPost(url, payload).then((data: OrganizationMember) => data);
};

export const removeUserFromOrg = (
  orgId: string,
  userId: string
): Promise<ApiMessageResponse> => {
  const url = ADMIN_ENDPOINTS.ORG_MEMBER_BY_ID(orgId, userId);
  return apiDelete(url).then((data: ApiMessageResponse | undefined) => {
    return data ?? { message: 'Member removed' };
  });
};

export const setUserDefaultOrg = (orgId: string, userId: string): Promise<void> => {
  const url = ADMIN_ENDPOINTS.ORG_MEMBER_DEFAULT(orgId, userId);
  return apiPatch(url, {}).then(() => undefined);
};

export const unsetUserDefaultOrg = (orgId: string, userId: string): Promise<void> => {
  const url = ADMIN_ENDPOINTS.ORG_MEMBER_DEFAULT(orgId, userId);
  return apiPatch(url, { is_default: false }).then(() => undefined);
};

export const updateMemberRole = (
  orgId: string,
  userId: string,
  payload: UpdateMemberRoleRequest
): Promise<OrganizationMember> => {
  const url = ADMIN_ENDPOINTS.ORG_MEMBER_ROLE(orgId, userId);
  return apiPatch(url, payload).then((data: OrganizationMember) => data);
};

export const updateOrgMetadata = (
  orgId: string,
  payload: UpdateOrgMetadataRequest
): Promise<Organization> => {
  const url = ADMIN_ENDPOINTS.ORG_METADATA(orgId);
  return apiPut(url, payload).then((data: Organization) => data);
};

export const updateOrgSettings = (
  orgId: string,
  payload: UpdateOrgSettingsRequest
): Promise<Organization> => {
  const url = ADMIN_ENDPOINTS.ORG_SETTINGS(orgId);
  return apiPatch(url, payload).then((data: Organization) => data);
};
